import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_COLORS } from "./chartTokens";
import { InformationHoverIcon } from "./InformationHoverIcon";

type DataPoint = {
  name: string;
  value: number;
};

type Props = {
  title: string;
  subtitle?: string;
  info?: string;
  data: DataPoint[];
  orientation?: "vertical" | "horizontal";
  height?: number;
  colSpan?: number;
  emptyText?: string;
  unitLabel?: { singular: string; plural: string };
};

const CustomTooltip = ({
  active,
  payload,
  unitLabel,
}: TooltipProps<number, string> & { unitLabel: { singular: string; plural: string } }) => {
  if (!active || !payload || payload.length === 0) return null;
  const datum = payload[0];
  return (
    <div className="rounded-md border border-border bg-card p-3 shadow-md">
      <p className="text-sm font-semibold text-palomma-secondary">{datum.payload?.name}</p>
      <p className="text-sm text-muted-foreground">
        {datum.value} {datum.value === 1 ? unitLabel.singular : unitLabel.plural}
      </p>
    </div>
  );
};

const CategoryBarChartCard = ({
  title,
  subtitle,
  info,
  data,
  orientation = "vertical",
  height = 260,
  colSpan = 1,
  emptyText = "Sin búsquedas en este período",
  unitLabel = { singular: "búsqueda", plural: "búsquedas" },
}: Props) => {
  const hasData = data.length > 0;
  const tickFill = CHART_COLORS.axis;
  const labelFill = CHART_COLORS.axis;

  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="mb-4">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-palomma-secondary">{title}</h3>
            {info && <InformationHoverIcon information={info} />}
          </div>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>

        {!hasData ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
        ) : orientation === "horizontal" ? (
          <div style={{ height: Math.max(height, data.length * 40 + 40) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 8, right: 32, bottom: 8, left: 8 }}
                barCategoryGap="20%"
              >
                <CartesianGrid
                  horizontal={false}
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.tooltipBorder}
                />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: tickFill }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  width={120}
                  tickFormatter={(label: string) =>
                    label.length > 16 ? `${label.slice(0, 15)}…` : label
                  }
                  tick={{ fontSize: 12, fill: tickFill }}
                />
                <Tooltip
                  content={<CustomTooltip unitLabel={unitLabel} />}
                  cursor={{ fill: "transparent" }}
                />
                <Bar
                  dataKey="value"
                  fill={CHART_COLORS.primary}
                  radius={[0, 4, 4, 0]}
                  maxBarSize={24}
                >
                  <LabelList
                    dataKey="value"
                    position="right"
                    style={{ fontSize: 12, fill: labelFill }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ height }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 20, right: 16, bottom: 8, left: 0 }}
                barCategoryGap="25%"
              >
                <CartesianGrid
                  vertical={false}
                  strokeDasharray="3 3"
                  stroke={CHART_COLORS.tooltipBorder}
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  tick={{ fontSize: 12, fill: tickFill }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                  width={32}
                  tick={{ fontSize: 12, fill: tickFill }}
                />
                <Tooltip
                  content={<CustomTooltip unitLabel={unitLabel} />}
                  cursor={{ fill: "transparent" }}
                />
                <Bar
                  dataKey="value"
                  fill={CHART_COLORS.primary}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={56}
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    style={{ fontSize: 12, fill: labelFill }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryBarChartCard;
